import React, { useEffect, useState } from 'react';
import { LayoutDashboard, Receipt, Key, Users, Settings, Bot, MoreVertical, Plus, X, AlertTriangle, CheckCircle, Info, MousePointerClick, TrendingUp, ShieldAlert, DollarSign, Activity, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface Agent {
  name: string;
  status: string;
  outputType: string;
  description: string;
  created: string;
}
interface AssessmentItem {
  label: string;
  value: string;
  source?: string;
}
interface AgentDetail {
  dataSources: string[];
  prompt: string;
  score: number;
  riskLevel: 'Low' | 'Moderate' | 'High';
  summary: string;
  assessments: AssessmentItem[];
  flags: string[];
}
interface ARData {
  outstanding: string;
  current: number;
  days30: number;
  days60: number;
  days90: number;
  terms: string;
  limit: string;
  utilization: number;
}
const SCOUT_AVATAR = 'https://cdn.magicpatterns.com/uploads/qugbTMfhfNKk42Zw4QditN/Icon_Scout.png';
const scoutInsights: Record<string, string> = {
  // Construction & Building Materials
  'Permit Activity': '3 new permits in Collin County totaling $2.1M, up 40% from last quarter. Project pipeline shows significant growth momentum across residential and commercial segments.',
  'Legal Filings': "2 mechanic's liens totaling $73K filed in the last 90 days. Payment disputes with subcontractors are creating legal exposure alongside 18% of AR aging past 60 days.",
  'Ownership Risk': 'Acquired by PE platform in 2024, bolted on 4 companies. Leverage at 4.2x EBITDA reflects aggressive growth strategy with integration still underway.',
  'SEC Investigation Risk': 'Active SEC investigation into accounting practices at model home developments. Subpoenas issued for financial records related to "light treason" allegations. CFO resigned abruptly citing "family matters" while liquidating stock holdings.',
  'Project Completion & Delivery Risk': 'Pattern of stalled developments emerging. "Sudden Valley" project remains 60% incomplete after 3 years. Model home in Iraq never delivered. History of cost overruns and timeline delays creating customer satisfaction issues.',
  // Manufacturing
  'Supply Chain Risk': 'Sourcing 65% of raw materials from 2 overseas suppliers. Steel prices up 12% YoY, creating margin pressure alongside concentrated vendor dependency.',
  'Environmental Compliance': 'No violations in 3 years, one minor citation resolved. All permits current with clean operational compliance record.',
  'Labor Risk': 'Union contract expires in 30 days with strike authorization vote pending. Labor disruption risk coincides with peak production season.',
  'Stock Performance': 'Stock up 100% YTD following strong earnings beat and enterprise customer wins. Recent profitability milestone and AWS partnership announcement driving institutional buying. Trading at 24x forward P/E reflects high growth expectations and market confidence in compression technology adoption.',
  // Distribution
  'Fleet Risk': 'Fleet averaging 7.2 years with 3 recent DOT maintenance flags. Aging assets are showing operational strain with compliance issues emerging.',
  'Warehouse Exposure': '10-year lease on new 100K sqft facility. Inventory turning at 8.5x vs 6.2x industry average shows efficient operations supporting expansion.',
  'Customer Concentration': 'Heavy reliance on large retail partnerships evident from case studies and press coverage. Website prominently features Staples and Office Depot as key customers, with limited diversification into other sectors visible in public materials.',
  'Licensing Compliance': 'Active licenses across 14 states. DOT authority current with no pending regulatory actions or compliance gaps.',
  // Food & Beverage
  'FDA Inspection Risk': 'FDA warning letter in January for sanitation issues. Class II recall last year indicates ongoing quality control challenges requiring remediation.',
  'Commodity Exposure': 'Cocoa up 40% YoY, squeezing margins. Input cost inflation is outpacing pricing adjustments, compressing profitability.',
  'Brand Sentiment': '4.4 stars across reviews, 78% positive social sentiment. Strong consumer perception with no food safety red flags in recent coverage.',
  'Regulatory Investigation': 'Unusual DEA interest in supply chain logistics. Multiple franchisees questioned about "distribution practices." Owner under surveillance for suspected involvement in "alternative business operations." Federal asset seizure risk escalating.',
  'Franchise & Location Risk': 'Closing 3 underperforming Midwest hubs, breaking 2 leases early. Footprint contraction reflects regional performance challenges and lease obligation exposure.',
  // Software (SaaS)
  'Hiring Signals': 'Grew from 42 to 53 employees in 6 months. 12 open engineering roles and new Austin office indicate aggressive scaling with strong hiring velocity.',
  'Service & Product Offering Changes': 'Added enterprise security tier, announced SOC2 Type II. Product evolution and compliance certifications support upmarket positioning.',
  'Ownership Changes': 'Acquired EndFrame for $8M. Raviga Capital partner added to board while CTO Gilfoyle steps back, signaling ownership and leadership transition.',
  'Security Signals': 'No public breaches. Hiring 3 security engineers with SOC2 and ISO 27001 current demonstrates mature security posture.',
  // Agriculture
  'Commodity Price Risk': 'Corn and soybean futures down 15%. Commodity price decline combined with limited hedging activity exposes revenue to market volatility.',
  'Weather Exposure': 'Severe drought in primary region. 30% reduction in expected yield directly impacts revenue capacity and debt service ability.',
  'Agritourism Risk': 'Bed & breakfast operations generating 18% of revenue but facing liability issues. Recent guest complaints about "authenticity" of historical farm experience. Health department flagged beet wine production. Event bookings down 40% after viral TikTok about "unusual" accommodations.',
  'Subsidy Risk': 'Heavily reliant on federal crop insurance and subsidies, currently stable. No EPA violations with regulatory environment remaining supportive.',
  // Chemical & Industrial
  'Safety Compliance': 'Recently cited for improper hazmat storage. Moderate fine reflects operational compliance gaps requiring corrective action.',
  'Environmental Liability': 'Involved in localized groundwater contamination probe. Environmental investigation creates potential remediation liability and regulatory exposure.',
  'Raw Material Risk': 'Petrochemical feedstocks up 22%. Input cost inflation is compressing margins as pricing power remains limited with buyers.',
  'Incident Risk': 'Multiple reports of "containment protocol failures" at island facilities. Viral social media posts about "specimens" escaping secure areas creating severe reputational damage. Three wrongful death lawsuits pending from site incidents.',
  // Equipment Rental & Leasing
  'Fleet Utilization': 'Fleet utilization dropped to 58% (down from 72%) due to construction slowdown. Declining demand is reducing revenue per asset.',
  'Maintenance Exposure': 'Average fleet age creeping to 6 years. Aging equipment base will require increased maintenance capital expenditure.',
  'Product Liability': 'Catastrophic failure rate 3x industry average. 47 injury claims this year from "premature detonation" and "gravity-related malfunctions." Recall issued for entire anvil product line. Insurance carrier threatening non-renewal.',
  'Market Demand': 'Infrastructure spending booming. Secured master lease for major highway project provides strong demand visibility and revenue stability.',
  // Healthcare
  'Regulatory & Compliance Risk': 'Under routine CMS audit. No red flags or billing suspensions with standard regulatory oversight proceeding normally.',
  'Reimbursement & Payer Mix Risk': 'Strong commercial payer mix at 62% with average reimbursement cycle of 32 days. Consistent cash flow from diversified payer base supporting operational stability.',
  'Malpractice & Litigation Exposure': 'Clean malpractice record with no active litigation. Strong patient satisfaction scores (4.6/5.0) and comprehensive liability coverage demonstrate low litigation risk.',
  'Staffing & Workforce Risk': 'Nursing turnover at 35%. Heavy reliance on expensive travel nurses is inflating labor costs and reducing operational stability.',
  // Retail
  'Environmental Impact & ESG Risk': 'EPA fines totaling $12M for illegal waste dumping. Viral documentary exposing "planet-scale pollution" causing consumer boycotts. Sustainability report reveals waste management operations exceed landfill capacity. Major institutional investors divesting on ESG concerns.',
  'Foot Traffic & Same-Store Sales': 'Same-store sales down 8% YoY. Foot traffic declining at mall locations reflects broader brick-and-mortar headwinds.',
  'E-Commerce & Channel Risk': 'Online traffic grew 45% this quarter, offsetting brick-and-mortar declines. Digital channel growth is compensating for physical store weakness.',
  'Stock Performance & Market Sentiment': 'Stock down 32% over 12 months, hitting 5-year lows. Dividend cut 50% last quarter. Credit rating downgraded to BB+ on liquidity concerns. Institutional ownership declining as funds exit retail exposure.',
  // Transportation & Logistics
  'Fleet Compliance': 'Good news, everyone! The delivery fleet is showing its age at 28% out-of-service rate (vs 21% national average). Even interplanetary delivery companies need to maintain their ships — increased DOT scrutiny is triggering delays.',
  'Operating Costs': "The ship's fuel costs are spiking with diesel prices up, but contracts lack adequate fuel surcharges. Unlike dark matter, these costs can't be ignored — margins are getting squeezed as the crew absorbs the price increases.",
  'Labor Risk': "Driver turnover over 90% — looks like Fry isn't the only one having trouble keeping delivery crew around. Massive sign-on bonuses to replace pilots and drivers are eating into profits faster than Nibbler eats ham.",
  'Customer Concentration (Transportation)': 'Good news, everyone! Won dedicated lane contract with Amazon for 50 trucks. This is bigger than that time we delivered to Omicron Persei 8 — guaranteed volume provides major revenue stability for the delivery business.',
  // Default agents
  'Financial Risk': 'Revenue down 8% YoY ($12.1M → $11.1M). Net margin compressed from 5.1% to 3.2% while DBT remains stable at 14 days.',
  'Operations Risk': 'Headcount steady at ~38. Running lean in warehouse (3 staff) with single location in Scranton creating operational concentration.',
  'Reputational Risk': '4.2 stars on Google (47 reviews). 2 BBB complaints in 24 months (both resolved) with no negative press coverage.',
  'PE Ownership & Sponsor Risk': 'Subsidiary of Sabre Corp (public). No direct PE involvement with parent leverage at 2.1x EBITDA indicating moderate corporate debt.',
  'Location/Geopolitical Risk': 'All domestic operations in Northeast US. Clean on sanctions with no natural disaster exposure or geopolitical risk factors.'
};
const defaultAgents: Agent[] = [{
  name: 'Financial Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Assesses financial health and stability via public filings and tax liens',
  created: 'March 19, 2026'
}, {
  name: 'Operations Risk',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Assesses operational stability and execution capability via public workforce data',
  created: 'March 19, 2026'
}, {
  name: 'Reputational Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors brand sentiment and customer satisfaction via public reviews and complaints',
  created: 'March 19, 2026'
}, {
  name: 'Ownership Risk',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Assesses private equity ownership and leverage risk via state SOS filings and Crunchbase',
  created: 'March 19, 2026'
}];
const buildingMaterialsAgents: Agent[] = [{
  name: 'SEC Investigation Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors securities fraud investigations, accounting irregularities, and regulatory enforcement actions',
  created: 'March 19, 2026'
}, {
  name: 'Permit Activity',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks building permits, project starts, and construction pipeline to assess customer demand and payment capacity',
  created: 'March 19, 2026'
}, {
  name: 'Legal Filings',
  status: 'Active',
  outputType: 'Structured JSON',
  description: "Monitors mechanic's liens, judgments, and county court filings to detect legal risk and payment disputes",
  created: 'March 19, 2026'
}, {
  name: 'Ownership Risk',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Identifies PE-backed rollups and acquisition activity that may signal leverage risk or ownership changes',
  created: 'March 19, 2026'
}];
const manufacturingAgents: Agent[] = [{
  name: 'Stock Performance',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks stock price trends, analyst ratings, institutional ownership, and market sentiment for public companies',
  created: 'March 19, 2026'
}, {
  name: 'Supply Chain Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors supplier concentration, raw material pricing, and vendor financial health',
  created: 'March 19, 2026'
}, {
  name: 'Environmental Compliance',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks EPA violations, OSHA citations, and environmental permits',
  created: 'March 19, 2026'
}, {
  name: 'Labor Risk',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Monitors union activity, labor disputes, turnover, and hiring trends',
  created: 'March 19, 2026'
}];
const distributionAgents: Agent[] = [{
  name: 'Customer Concentration',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Infers customer concentration and revenue dependency using publicly available information',
  created: 'March 19, 2026'
}, {
  name: 'Fleet Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors DOT violations, fleet age, fuel cost exposure, and delivery performance',
  created: 'March 19, 2026'
}, {
  name: 'Warehouse Exposure',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks warehouse lease obligations, inventory turnover, and storage capacity',
  created: 'March 19, 2026'
}, {
  name: 'Licensing Compliance',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Monitors business licenses, permits, and regulatory actions',
  created: 'March 19, 2026'
}];
const foodBeverageAgents: Agent[] = [{
  name: 'Regulatory Investigation',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors DEA, FBI, and law enforcement investigations into business operations',
  created: 'March 19, 2026'
}, {
  name: 'FDA Inspection Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks FDA warning letters, health department inspections, and recall history',
  created: 'March 19, 2026'
}, {
  name: 'Commodity Exposure',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors ingredient cost volatility, supplier stability, and import dependencies',
  created: 'March 19, 2026'
}, {
  name: 'Brand Sentiment',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Tracks consumer reviews, social media sentiment, and food safety mentions',
  created: 'March 19, 2026'
}];
const softwareAgents: Agent[] = [{
  name: 'Stock Performance',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks stock price trends, analyst ratings, institutional ownership, and market sentiment for public companies',
  created: 'March 19, 2026'
}, {
  name: 'Hiring Signals',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks publicly available signals indicating company growth, new office openings, and hiring velocity',
  created: 'March 19, 2026'
}, {
  name: 'Ownership Changes',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks ownership changes, mergers, acquisitions, PE involvement, and leadership shifts',
  created: 'March 19, 2026'
}, {
  name: 'Security Signals',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Monitors cybersecurity incidents, compliance initiatives, and security hiring signals',
  created: 'March 19, 2026'
}];
const agricultureAgents: Agent[] = [{
  name: 'Agritourism Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Assesses revenue from non-farming activities including bed & breakfast, events, and tourism operations',
  created: 'March 19, 2026'
}, {
  name: 'Commodity Price Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors crop yields, futures pricing, and hedging strategies',
  created: 'March 19, 2026'
}, {
  name: 'Weather Exposure',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks regional weather patterns, drought indices, and natural disaster risks',
  created: 'March 19, 2026'
}, {
  name: 'Subsidy Risk',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Monitors federal crop insurance, subsidies, and EPA agricultural compliance',
  created: 'March 19, 2026'
}];
const chemicalIndustrialAgents: Agent[] = [{
  name: 'Incident Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors negative press coverage, containment breaches, and public safety incidents',
  created: 'March 19, 2026'
}, {
  name: 'Safety Compliance',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks OSHA citations, safety incidents, and hazardous material handling',
  created: 'March 19, 2026'
}, {
  name: 'Environmental Liability',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors EPA investigations, pollution fines, and remediation liabilities',
  created: 'March 19, 2026'
}, {
  name: 'Raw Material Risk',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Assesses petrochemical feedstock costs and supplier stability',
  created: 'March 19, 2026'
}];
const equipmentRentalAgents: Agent[] = [{
  name: 'Product Liability',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Assesses product recalls, safety defects, and liability claims from equipment failures',
  created: 'March 19, 2026'
}, {
  name: 'Fleet Utilization',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors equipment utilization rates, idle time, and regional demand',
  created: 'March 19, 2026'
}, {
  name: 'Maintenance Exposure',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks fleet age, maintenance CapEx requirements, and asset depreciation',
  created: 'March 19, 2026'
}, {
  name: 'Market Demand',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Monitors regional construction starts and infrastructure spending cycles',
  created: 'March 19, 2026'
}];
const healthcareAgents: Agent[] = [{
  name: 'Malpractice Exposure',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Assesses active malpractice suits, settlements, and liability insurance coverage',
  created: 'March 19, 2026'
}, {
  name: 'Regulatory Compliance',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks CMS audits, Medicare/Medicaid billing suspensions, and HIPAA violations',
  created: 'March 19, 2026'
}, {
  name: 'Reimbursement Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors shifts in payer mix (Private vs Medicaid) and reimbursement cycles',
  created: 'March 19, 2026'
}, {
  name: 'Staffing Risk',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Tracks nursing turnover, reliance on travel nurses, and labor cost inflation',
  created: 'March 19, 2026'
}];
const retailAgents: Agent[] = [{
  name: 'Environmental Impact',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks environmental violations, waste management failures, and ESG-related reputational damage',
  created: 'March 19, 2026'
}, {
  name: 'Foot Traffic',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors location-based foot traffic data and YoY same-store sales trends',
  created: 'March 19, 2026'
}, {
  name: 'E-Commerce Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Assesses online sales growth, omnichannel adaptation, and digital market share',
  created: 'March 19, 2026'
}, {
  name: 'Stock Performance',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Tracks stock price trends, analyst ratings, institutional ownership, and market sentiment for public companies',
  created: 'March 19, 2026'
}];
const transportationAgents: Agent[] = [{
  name: 'Customer Concentration (Transportation)',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks dedicated lane contracts, spot market reliance, and key customer health',
  created: 'March 19, 2026'
}, {
  name: 'Fleet Compliance',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks DOT out-of-service rates, safety violations, and inspection history',
  created: 'March 19, 2026'
}, {
  name: 'Operating Costs',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors diesel price exposure, fuel surcharge adequacy, and maintenance costs',
  created: 'March 19, 2026'
}, {
  name: 'Labor Risk',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Assesses driver turnover rates, wage inflation, and recruitment costs',
  created: 'March 19, 2026'
}];
// All these already have 4 agents, so no changes needed to the arrays themselves
const industryAgents: Record<string, Agent[]> = {
  Agriculture: agricultureAgents,
  'Chemical & Industrial': chemicalIndustrialAgents,
  'Construction & Building Materials': buildingMaterialsAgents,
  Distribution: distributionAgents,
  'Equipment Rental & Leasing': equipmentRentalAgents,
  'Food & Beverage': foodBeverageAgents,
  Healthcare: healthcareAgents,
  Manufacturing: manufacturingAgents,
  Retail: retailAgents,
  'Software (SaaS)': softwareAgents,
  'Transportation & Logistics': transportationAgents
};
const defaultAgentDetails: Record<string, AgentDetail> = {
  Financial: {
    dataSources: ['State Annual Reports', 'Public Tax Liens', 'County Court Records'],
    prompt: `Analyze the subject company's financial health using public filings and tax liens.

Evaluate:
1. Revenue trend (growing, stable, declining)
2. Profit margins and net income trajectory
3. Debt-to-equity ratio and leverage
4. Payment behavior (DBT days, delinquency rate)
5. Cash flow adequacy

Return a risk score (0-100) and structured assessment with key findings.`,
    score: 62,
    riskLevel: 'Moderate',
    summary: 'Revenue declined 8% YoY with margin compression from 5.1% to 3.2%.',
    assessments: [{
      label: 'Revenue',
      value: 'Down 8% YoY ($12.1M → $11.1M)',
      source: 'State Annual Reports'
    }, {
      label: 'Net Margin',
      value: '3.2%, down from 5.1%',
      source: 'State Annual Reports'
    }, {
      label: 'Leverage',
      value: '1.8x debt-to-equity',
      source: 'State Annual Reports'
    }, {
      label: 'Tax Liens',
      value: 'None active',
      source: 'County Court Records'
    }],
    flags: ['Declining revenue', 'Margin compression']
  },
  Operations: {
    dataSources: ['LinkedIn Profiles', 'Glassdoor Reviews', 'Indeed / LinkedIn Jobs'],
    prompt: `Assess the subject company's operational stability using public workforce data, job postings, and employee reviews.

Evaluate:
1. Headcount trend (growing, stable, shrinking)
2. Key department staffing
3. Leadership stability and tenure
4. Hiring activity vs. layoff signals
5. Employee sentiment and operational risk

Return a risk score (0-100) and structured assessment.`,
    score: 55,
    riskLevel: 'Moderate',
    summary: 'Headcount stable at ~38 but single-location dependency with lean warehouse staffing.',
    assessments: [{
      label: 'Headcount',
      value: '~38 employees, flat 12 months',
      source: 'LinkedIn Profiles'
    }, {
      label: 'Sales Team',
      value: '8 reps, fully staffed',
      source: 'LinkedIn Profiles'
    }, {
      label: 'Leadership',
      value: 'Regional Mgr 12+ yr tenure',
      source: 'LinkedIn Profiles'
    }, {
      label: 'Open Roles',
      value: '1 posting (Jr. Salesman)',
      source: 'Indeed / LinkedIn Jobs'
    }, {
      label: 'Employee Sentiment',
      value: '3.4/5.0 — "fun but chaotic"',
      source: 'Glassdoor Reviews'
    }],
    flags: ['Single location dependency', 'Lean warehouse staffing']
  },
  Reputational: {
    dataSources: ['Google Reviews', 'Yelp', 'BBB'],
    prompt: `Monitor the subject company's brand reputation using public reviews and complaint records.

Evaluate:
1. Overall review sentiment (Google, Yelp, BBB)
2. Review volume and recency
3. BBB complaint history and resolution rate
4. Customer satisfaction trend

Return a risk score (0-100) and structured assessment.`,
    score: 78,
    riskLevel: 'Low',
    summary: 'Strong local reputation with 4.2 stars on Google and only 2 resolved BBB complaints in 24 months.',
    assessments: [{
      label: 'Google Reviews',
      value: '4.2 ★ (47 reviews)',
      source: 'Google Reviews'
    }, {
      label: 'Yelp',
      value: '3.8 ★ (12 reviews)',
      source: 'Yelp'
    }, {
      label: 'BBB Complaints',
      value: '2 in 24 mo, both resolved',
      source: 'BBB'
    }],
    flags: []
  },
  Ownership: {
    dataSources: ['Crunchbase', 'State SOS Filings', 'Google News'],
    prompt: `Assess whether the subject company has private equity ownership and evaluate associated leverage and sponsor risk.

Evaluate:
1. Ownership structure (PE-backed, founder-owned, public)
2. If PE-backed: sponsor identity, fund vintage, hold period
3. Leverage levels relative to revenue
4. Recent ownership changes

Return a risk score (0-100) and structured assessment.`,
    score: 30,
    riskLevel: 'Low',
    summary: 'Subsidiary of publicly traded Sabre Corp with no direct PE involvement.',
    assessments: [{
      label: 'Ownership',
      value: 'Subsidiary of Sabre Corp (public)',
      source: 'Google News'
    }, {
      label: 'PE Involvement',
      value: 'None detected',
      source: 'Crunchbase'
    }, {
      label: 'Corporate Status',
      value: 'Active / Good Standing',
      source: 'State SOS Filings'
    }],
    flags: ['Parent company dependency']
  },
  'Location/Geopolitical': {
    dataSources: ['OFAC SDN List', 'FEMA Disaster Declarations', 'Google Maps'],
    prompt: `Assess the subject company's geopolitical exposure and location-based risk using address data, sanctions screening, and regional risk factors.

Evaluate:
1. Primary operating locations
2. Sanctions or restricted party screening
3. Regional economic stability
4. Natural disaster exposure

Return a risk score (0-100) and structured assessment.`,
    score: 85,
    riskLevel: 'Low',
    summary: 'Domestic-only operations in Northeast US with clean sanctions screening.',
    assessments: [{
      label: 'Locations',
      value: 'Scranton, PA (HQ); Nashua, NH',
      source: 'Google Maps'
    }, {
      label: 'Sanctions',
      value: 'Clear — OFAC, BIS',
      source: 'OFAC SDN List'
    }, {
      label: 'Disaster Risk',
      value: 'Low — no major exposure',
      source: 'FEMA Disaster Declarations'
    }],
    flags: []
  }
};
const buildingMaterialsDetails: Record<string, AgentDetail> = {
  'Permit Activity': {
    dataSources: ['County Permit Database', 'Google News'],
    prompt: `Analyze the subject contractor or builder's active permit and project pipeline to assess near-term demand and payment capacity.

Evaluate:
1. Active building permits (count, value, type)
2. Project pipeline trend (growing, stable, declining)
3. Geographic concentration of projects

Return a risk score (0-100) and assessment of project pipeline health.`,
    score: 74,
    riskLevel: 'Low',
    summary: '12 active permits totaling $8.4M with pipeline up 15% vs. prior quarter.',
    assessments: [{
      label: 'Active Permits',
      value: '12 permits, $8.4M total value',
      source: 'County Permit Database'
    }, {
      label: 'Project Mix',
      value: '8 residential, 4 commercial',
      source: 'County Permit Database'
    }, {
      label: 'Pipeline Trend',
      value: 'Up 15% vs. prior quarter',
      source: 'County Permit Database'
    }],
    flags: []
  },
  'Legal Filings': {
    dataSources: ['County Court Records', 'PACER'],
    prompt: `Screen the subject contractor for mechanic's liens, judgments, lawsuits, and other county-level legal filings that indicate payment disputes or financial distress.

Evaluate:
1. Mechanic's lien filings (filed by or against the company)
2. Judgment history and amounts
3. Active lawsuits or disputes

Return a risk score (0-100) and structured assessment of legal exposure.`,
    score: 48,
    riskLevel: 'Moderate',
    summary: "Two mechanic's liens totaling $73K filed against the company in the last 6 months.",
    assessments: [{
      label: "Mechanic's Liens",
      value: '2 filed against in 6 mo ($45K, $28K)',
      source: 'County Court Records'
    }, {
      label: 'Judgments',
      value: '1 satisfied judgment ($12K, 2024)',
      source: 'County Court Records'
    }, {
      label: 'Active Lawsuits',
      value: '1 — subcontractor payment dispute',
      source: 'PACER'
    }],
    flags: ["Recent mechanic's liens", 'Active payment dispute']
  },
  'Ownership Risk': {
    dataSources: ['Crunchbase', 'Google News', 'State SOS Filings'],
    prompt: `Determine whether the subject company is part of a PE-backed rollup or has recently been acquired.

Evaluate:
1. Ownership structure
2. Recent acquisition or merger activity
3. Sponsor identity and rollup strategy
4. Integration risk

Return a risk score (0-100) and structured assessment.`,
    score: 65,
    riskLevel: 'Moderate',
    summary: 'Acquired by Summit Partners platform in 2024 with 4 add-on acquisitions.',
    assessments: [{
      label: 'Ownership',
      value: 'PE-backed — Summit Partners (2024)',
      source: 'Crunchbase'
    }, {
      label: 'Rollup Strategy',
      value: 'Regional roofing consolidation (4 add-ons)',
      source: 'Google News'
    }],
    flags: ['Recent acquisition', 'Active integration risk']
  },
  'SEC Investigation Risk': {
    dataSources: ['SEC EDGAR', 'PACER', 'Google News'],
    prompt: `Monitor securities fraud investigations, accounting irregularities, and regulatory enforcement.
Evaluate:
1. Active SEC investigations or enforcement actions
2. Accounting restatements or irregularities
3. Executive departures during investigations
4. Insider trading patterns
Return a risk score (0-100) and structured assessment.`,
    score: 20,
    riskLevel: 'High',
    summary: 'Active SEC investigation into accounting practices with CFO resignation and suspicious insider trading.',
    assessments: [{
      label: 'SEC Status',
      value: 'Active investigation, subpoenas issued',
      source: 'SEC EDGAR'
    }, {
      label: 'Executive Changes',
      value: 'CFO resigned abruptly, liquidated all holdings',
      source: 'Google News'
    }, {
      label: 'Allegations',
      value: 'Accounting fraud at model home developments, "light treason" charges pending',
      source: 'PACER'
    }, {
      label: 'Project Issues',
      value: 'Sudden Valley project 60% incomplete, Iraq model home never delivered',
      source: 'Google News'
    }],
    flags: ['Active SEC investigation', 'Executive flight risk', 'Light treason allegations', 'Stalled developments']
  }
};
const manufacturingDetails: Record<string, AgentDetail> = {
  'Stock Performance': {
    dataSources: ['Yahoo Finance', 'Bloomberg Terminal', 'SEC EDGAR'],
    prompt: `Monitor stock performance, analyst sentiment, and market indicators for publicly traded companies.

Evaluate:
1. Stock price performance (YTD, 1-year, vs sector)
2. Analyst ratings and price targets
3. Institutional ownership trends
4. Trading volume and volatility
5. Credit rating changes

Return a risk score (0-100) and structured assessment.`,
    score: 45,
    riskLevel: 'Moderate',
    summary: 'Stock down 18% YTD with recent analyst downgrades citing margin pressure and labor uncertainty.',
    assessments: [{
      label: 'Stock Performance',
      value: '-18% YTD, -12% vs sector',
      source: 'Yahoo Finance'
    }, {
      label: 'Analyst Ratings',
      value: '3 downgrades this quarter',
      source: 'Bloomberg Terminal'
    }, {
      label: 'Valuation',
      value: '12x forward P/E (sector avg: 16x)',
      source: 'Yahoo Finance'
    }, {
      label: 'Institutional Ownership',
      value: 'Down 4% QoQ',
      source: 'SEC EDGAR'
    }],
    flags: ['Underperforming sector', 'Analyst downgrades']
  },
  'Supply Chain Risk': {
    dataSources: ['Google News', 'LinkedIn Profiles'],
    prompt: `Analyze supplier concentration and raw material pricing trends.

Evaluate:
1. Supplier concentration
2. Raw material cost trends
3. Geographic supply chain exposure

Return a risk score (0-100) and structured assessment.`,
    score: 68,
    riskLevel: 'Moderate',
    summary: 'Heavy reliance on two overseas suppliers with raw material costs up 12% YoY.',
    assessments: [{
      label: 'Supplier Concentration',
      value: 'High — 65% from 2 vendors',
      source: 'LinkedIn Profiles'
    }, {
      label: 'Geo Exposure',
      value: '30% of components from Asia',
      source: 'Google News'
    }],
    flags: ['Supplier concentration', 'Rising material costs']
  },
  'Environmental Compliance': {
    dataSources: ['OSHA Inspection Database', 'Google News'],
    prompt: `Track OSHA citations and environmental permits.

Evaluate:
1. Recent OSHA citations or safety incidents
2. Status of required environmental permits
3. Historical compliance trends

Return a risk score (0-100) and structured assessment.`,
    score: 85,
    riskLevel: 'Low',
    summary: 'Clean environmental record with no OSHA citations in 3 years.',
    assessments: [{
      label: 'OSHA Citations',
      value: '1 minor citation (resolved)',
      source: 'OSHA Inspection Database'
    }, {
      label: 'Permit Status',
      value: 'All active and compliant',
      source: 'Google News'
    }],
    flags: []
  },
  'Labor Risk': {
    dataSources: ['NLRB Filings', 'Indeed / LinkedIn Jobs', 'Glassdoor Reviews'],
    prompt: `Monitor union activity, labor disputes, and turnover.

Evaluate:
1. Active union contracts and expiration dates
2. NLRB filings or labor disputes
3. Hiring velocity for production roles

Return a risk score (0-100) and structured assessment.`,
    score: 40,
    riskLevel: 'High',
    summary: 'Union contract expires in 30 days with strike authorization vote pending.',
    assessments: [{
      label: 'Union Status',
      value: 'Contract expires in 30 days',
      source: 'NLRB Filings'
    }, {
      label: 'Labor Disputes',
      value: 'Strike authorization vote pending',
      source: 'NLRB Filings'
    }, {
      label: 'Hiring',
      value: 'Frozen for production roles',
      source: 'Indeed / LinkedIn Jobs'
    }],
    flags: ['Strike threat', 'Contract expiration']
  }
};
const distributionDetails: Record<string, AgentDetail> = {
  'Customer Concentration': {
    dataSources: ['Case Studies', 'Press Releases', 'News'],
    prompt: `Analyze [Company Name] and infer its customer concentration using only publicly available information. Identify the types of customers they serve, key industries, and any signals of concentration risk.

Evaluate:
1. Types of customers served (enterprise, SMB, consumer, etc.)
2. Key industries or sectors mentioned
3. Named partnerships or major customers
4. Signals of revenue concentration or dependency

Return a risk score (0-100) and structured assessment.`,
    score: 35,
    riskLevel: 'High',
    summary: 'Heavy reliance on large retail partnerships with limited sector diversification evident from public materials.',
    assessments: [{
      label: 'Key Customers',
      value: 'Staples, Office Depot featured prominently',
      source: 'Case Studies'
    }, {
      label: 'Customer Type',
      value: 'Large retail chains, office supply sector',
      source: 'Press Releases'
    }, {
      label: 'Sector Concentration',
      value: 'Limited diversification beyond retail',
      source: 'News'
    }],
    flags: ['High customer concentration', 'Key customer distress'],
    recommendation: 'Heavy reliance on a single struggling customer creates significant revenue risk — if that relationship falters, payment capacity drops fast.'
  },
  'Fleet Risk': {
    dataSources: ['FMCSA SAFER System', 'EIA Diesel Price Index'],
    prompt: `Monitor DOT violations, fleet age, fuel cost exposure, and delivery performance.

Evaluate:
1. DOT safety rating and recent violations
2. Average fleet age and maintenance signals
3. Exposure to fuel price volatility
4. On-time delivery metrics (if public)

Return a risk score (0-100) and structured assessment.`,
    score: 55,
    riskLevel: 'Moderate',
    summary: 'Aging fleet leading to increased maintenance costs and minor DOT violations.',
    assessments: [{
      label: 'DOT Violations',
      value: '3 vehicle maintenance flags',
      source: 'FMCSA SAFER System'
    }, {
      label: 'Fleet Age',
      value: 'Avg 7.2 years',
      source: 'FMCSA SAFER System'
    }, {
      label: 'Fuel Exposure',
      value: 'Unhedged, highly exposed',
      source: 'EIA Diesel Price Index'
    }],
    flags: ['Aging fleet', 'Maintenance violations'],
    recommendation: 'An aging fleet with increasing maintenance flags suggests a looming CapEx requirement that could drain cash reserves.'
  },
  'Warehouse Exposure': {
    dataSources: ['CoStar', 'State Annual Reports', 'IBISWorld'],
    prompt: `Track warehouse lease obligations, inventory turnover, and storage capacity.

Evaluate:
1. Recent lease signings or facility expansions
2. Inventory turnover ratio vs industry
3. Total square footage and utilization
4. Geographic distribution of warehouses

Return a risk score (0-100) and structured assessment.`,
    score: 82,
    riskLevel: 'Low',
    summary: 'Recently expanded capacity with a new 10-year lease; inventory turning efficiently.',
    assessments: [{
      label: 'Lease Obligations',
      value: 'New 100k sqft facility (10 yr)',
      source: 'CoStar'
    }, {
      label: 'Inventory Turnover',
      value: '8.5x (Industry avg: 6.2x)',
      source: 'State Annual Reports'
    }, {
      label: 'Footprint',
      value: '3 regional hubs, 250k sqft total',
      source: 'CoStar'
    }],
    flags: [],
    recommendation: 'Efficient inventory turnover and recent facility expansion signal a healthy, growing distribution business.'
  },
  'Licensing Compliance': {
    dataSources: ['State Licensing Databases', 'FMCSA SAFER System'],
    prompt: `Monitor business licenses, permits, and regulatory actions.

Evaluate:
1. Status of state business and distribution licenses
2. Interstate commerce authority (DOT)
3. Any pending regulatory actions or fines

Return a risk score (0-100) and structured assessment.`,
    score: 90,
    riskLevel: 'Low',
    summary: 'All state and federal licenses are active and in good standing.',
    assessments: [{
      label: 'State Licenses',
      value: 'Active across 14 states',
      source: 'State Licensing Databases'
    }, {
      label: 'DOT Authority',
      value: 'Active and compliant',
      source: 'FMCSA SAFER System'
    }],
    flags: [],
    recommendation: 'Active and compliant licenses across all operating states indicate a stable, legally sound business operation.'
  }
};
const foodBeverageDetails: Record<string, AgentDetail> = {
  'FDA Inspection Risk': {
    dataSources: ['FDA Warning Letters Database', 'State Health Dept Inspection Reports', 'USDA FSIS Recall Database'],
    prompt: `Track FDA warning letters, health department inspections, and recall history.

Evaluate:
1. Recent FDA warning letters or inspections
2. State/local health department scores
3. History of product recalls (Class I, II, III)
4. Import alerts or detentions

Return a risk score (0-100) and structured assessment.`,
    score: 25,
    riskLevel: 'High',
    summary: 'Recent FDA warning letter at primary processing facility regarding sanitation.',
    assessments: [{
      label: 'FDA Status',
      value: 'Warning Letter issued Jan 2026',
      source: 'FDA Warning Letters Database'
    }, {
      label: 'Recalls',
      value: 'Class II recall last year',
      source: 'USDA FSIS Recall Database'
    }, {
      label: 'Health Dept',
      value: 'Conditional pass at main plant',
      source: 'State Health Dept Inspection Reports'
    }],
    flags: ['FDA Warning Letter', 'Recent recall'],
    recommendation: 'Recent FDA warnings and recalls pose a severe threat to operational continuity and brand reputation, elevating default risk.'
  },
  'Commodity Exposure': {
    dataSources: ['USDA Market Reports', 'ImportGenius', 'State Annual Reports'],
    prompt: `Monitor ingredient cost volatility, supplier stability, and import dependencies.

Evaluate:
1. Exposure to volatile commodities (cocoa, sugar, wheat)
2. Reliance on imported ingredients
3. Supplier financial health
4. Supply chain disruptions

Return a risk score (0-100) and structured assessment.`,
    score: 60,
    riskLevel: 'Moderate',
    summary: 'Margins pressured by rising cocoa and sugar prices; suppliers stable.',
    assessments: [{
      label: 'Commodity Costs',
      value: 'Cocoa +40% YoY',
      source: 'USDA Market Reports'
    }, {
      label: 'Import Reliance',
      value: '30% from South America',
      source: 'ImportGenius'
    }],
    flags: ['Commodity price exposure'],
    recommendation: 'Unhedged exposure to volatile commodity prices is squeezing margins, which typically precedes stretched vendor payments.'
  },
  'Brand Sentiment': {
    dataSources: ['Google Reviews', 'Yelp', 'Twitter/X'],
    prompt: `Track consumer reviews, social media sentiment, and food safety mentions.

Evaluate:
1. Aggregate review scores across platforms
2. Social media sentiment trends
3. Mentions of foodborne illness or quality issues
4. Brand loyalty indicators

Return a risk score (0-100) and structured assessment.`,
    score: 70,
    riskLevel: 'Low',
    summary: 'Strong consumer brand loyalty despite recent price increases.',
    assessments: [{
      label: 'Public Reviews',
      value: '4.4 Avg across locations',
      source: 'Google Reviews'
    }, {
      label: 'Social Sentiment',
      value: '78% Positive',
      source: 'Twitter/X'
    }, {
      label: 'Safety Mentions',
      value: '0 significant flags',
      source: 'Twitter/X'
    }],
    flags: [],
    recommendation: 'Strong consumer brand loyalty and positive sentiment provide pricing power and revenue stability.'
  },
  'Regulatory Investigation': {
    dataSources: ['PACER', 'Google News', 'DEA Public Records'],
    prompt: `Monitor law enforcement investigations and criminal exposure.
Evaluate:
1. Active federal investigations (DEA, FBI, etc.)
2. Owner/executive legal exposure
3. Asset seizure risk
4. Franchisee involvement in investigations
Return a risk score (0-100) and structured assessment.`,
    score: 12,
    riskLevel: 'High',
    summary: 'Severe criminal investigation risk with DEA surveillance and potential RICO charges.',
    assessments: [{
      label: 'DEA Activity',
      value: 'Active investigation, owner "Gustavo" under 24/7 surveillance',
      source: 'DEA Public Records'
    }, {
      label: 'Franchisee Exposure',
      value: 'Multiple locations questioned about "distribution practices" and "special menu items"',
      source: 'Google News'
    }, {
      label: 'Asset Risk',
      value: 'High seizure probability, industrial laundry facility flagged',
      source: 'PACER'
    }, {
      label: 'Criminal Charges',
      value: 'RICO charges likely, "Los Pollos" suspected front operation',
      source: 'PACER'
    }],
    flags: ['Active DEA investigation', 'RICO exposure', 'Asset seizure risk', 'Suspected front operation']
  }
};
const softwareDetails: Record<string, AgentDetail> = {
  'Hiring Signals': {
    dataSources: ['LinkedIn Profiles', 'Indeed / LinkedIn Jobs', 'Google News'],
    prompt: `Track publicly available signals that indicate company growth and expansion.

Evaluate:
1. New office or location openings (including franchise expansion)
2. Hiring trends, especially increases in total headcount or roles in IT, operations, or security
3. Job postings volume and velocity over the past 3–6 months
4. Press releases or announcements related to geographic or service expansion

Return a score (0–100) indicating expansion momentum, key signals detected, and a short summary of growth trajectory.`,
    score: 72,
    riskLevel: 'Low',
    summary: 'Rapid headcount growth and geographic expansion signal strong revenue momentum and increasing operational complexity.',
    assessments: [{
      label: 'Headcount Growth',
      value: '+25% in last 6 months',
      source: 'LinkedIn Profiles'
    }, {
      label: 'Open Roles',
      value: '12 new engineering roles posted',
      source: 'Indeed / LinkedIn Jobs'
    }, {
      label: 'Locations',
      value: 'New Austin, TX office announced',
      source: 'Google News'
    }],
    flags: [],
    recommendation: 'Rapid headcount growth and geographic expansion signal strong revenue momentum and increasing operational complexity.'
  },
  'Ownership Changes': {
    dataSources: ['SEC EDGAR', 'Crunchbase', 'PitchBook'],
    prompt: `Track ownership changes, mergers, acquisitions, and structural shifts using publicly available data.

Evaluate:
1. Recent acquisitions or companies acquired
2. Mergers or consolidation activity
3. Private equity or new investor involvement
4. Leadership changes tied to ownership or strategic direction

Return a score (0–100) indicating level of structural change, key events detected, and summary of how the organization is evolving.`,
    score: 45,
    riskLevel: 'Moderate',
    summary: 'Recent acquisition activity and board-level changes indicate the company is in a transitional phase where integration risk and strategic direction are uncertain.',
    assessments: [{
      label: 'Acquisitions',
      value: 'Acquired EndFrame for $8M',
      source: 'SEC EDGAR'
    }, {
      label: 'Board Changes',
      value: 'New board member from Raviga Capital',
      source: 'PitchBook'
    }, {
      label: 'Leadership',
      value: 'CTO transition (Gilfoyle stepping back)',
      source: 'Crunchbase'
    }],
    flags: ['Recent acquisition', 'Leadership transition'],
    recommendation: 'Recent acquisition activity and board-level changes indicate the company is in a transitional phase where integration risk and strategic direction are uncertain.'
  },
  'Security Signals': {
    dataSources: ['Google News', 'GitHub', 'LinkedIn Profiles'],
    prompt: `Track publicly available signals related to cybersecurity, risk exposure, and compliance activity.

Evaluate:
1. Mentions of breaches, ransomware, or security incidents
2. Hiring for security or compliance roles
3. Compliance initiatives (SOC2, HIPAA, ISO, etc.)
4. News, blogs, or customer feedback related to security concerns or outages

Return a score (0-100) indicating urgency or opportunity, key signals detected, and summary of current security posture or activity.`,
    score: 80,
    riskLevel: 'Low',
    summary: 'Active investment in security hiring and current compliance certifications indicate a mature security posture with low incident risk.',
    assessments: [{
      label: 'Incidents',
      value: 'No public breaches detected',
      source: 'Google News'
    }, {
      label: 'Hiring',
      value: 'Actively hiring 3 security engineers',
      source: 'LinkedIn Profiles'
    }, {
      label: 'Compliance',
      value: 'SOC2 and ISO 27001 certifications current',
      source: 'GitHub'
    }],
    flags: [],
    recommendation: 'Active investment in security hiring and current compliance certifications indicate a mature security posture with low incident risk.'
  },
  'Stock Performance': {
    dataSources: ['Yahoo Finance', 'Bloomberg Terminal', 'SEC EDGAR'],
    prompt: `Monitor stock performance, analyst sentiment, and market indicators for publicly traded companies.

Evaluate:
1. Stock price performance (YTD, 1-year, vs sector)
2. Analyst ratings and price targets
3. Institutional ownership trends
4. Trading volume and volatility
5. Credit rating changes

Return a risk score (0-100) and structured assessment.`,
    score: 88,
    riskLevel: 'Low',
    summary: 'Strong post-IPO performance with stock up 67% from debut price, driven by enterprise adoption and profitability milestone.',
    assessments: [{
      label: 'Stock Performance',
      value: '$28/share (IPO: $14, +100% YTD)',
      source: 'Yahoo Finance'
    }, {
      label: 'Trading Activity',
      value: 'High volume, stable trading',
      source: 'Bloomberg Terminal'
    }, {
      label: 'Institutional Ownership',
      value: 'Up 18% QoQ, major tech funds entering',
      source: 'SEC EDGAR'
    }, {
      label: 'Analyst Coverage',
      value: '8 Buy, 2 Outperform, 1 Hold',
      source: 'Bloomberg Terminal'
    }],
    flags: []
  }
};
const agricultureDetails: Record<string, AgentDetail> = {
  'Agritourism Risk': {
    dataSources: ['Yelp', 'Health Department Records', 'Google Reviews'],
    prompt: `Assess revenue diversification through agritourism, events, and hospitality.
Evaluate:
1. Revenue contribution from non-farming activities
2. Guest satisfaction and review trends
3. Health and safety compliance for hospitality operations
4. Liability exposure from events and lodging
Return a risk score (0-100) and structured assessment.`,
    score: 55,
    riskLevel: 'Moderate',
    summary: 'Agritourism revenue declining due to negative reviews and health department concerns about unlicensed food production.',
    assessments: [{
      label: 'Revenue Mix',
      value: '18% from B&B and "Beet Experience" events',
      source: 'Google Reviews'
    }, {
      label: 'Guest Reviews',
      value: '2.8 stars, complaints about "authenticity" and "beet-heavy menu"',
      source: 'Yelp'
    }, {
      label: 'Health Compliance',
      value: 'Flagged for unlicensed beet wine production, "Schrute Farms Mose Special" under review',
      source: 'Health Department Records'
    }, {
      label: 'Booking Trend',
      value: 'Down 40% after viral TikTok about "irrigation ditch swimming" and "manure tours"',
      source: 'Google Reviews'
    }],
    flags: ['Declining bookings', 'Health violations', 'Reputational damage', 'Unlicensed beet wine']
  },
  'Commodity Price Risk': {
    dataSources: ['CME Futures', 'State Annual Reports'],
    prompt: `Analyze crop yields, futures pricing, and hedging strategies.
Evaluate:
1. Exposure to volatile crop prices
2. Effectiveness of hedging strategies
3. Impact of price drops on margins
Return a risk score (0-100) and structured assessment.`,
    score: 65,
    riskLevel: 'Moderate',
    summary: 'Corn and soybean futures down 15%; margins squeezed due to ineffective hedging.',
    assessments: [{
      label: 'Commodity Prices',
      value: 'Corn/Soy down 15%',
      source: 'CME Futures'
    }, {
      label: 'Hedging',
      value: 'Under-hedged for current season',
      source: 'State Annual Reports'
    }],
    flags: ['Unhedged commodity exposure'],
    recommendation: 'Unhedged exposure to falling crop prices is severely compressing margins, likely leading to cash flow constraints post-harvest.'
  },
  'Weather Exposure': {
    dataSources: ['US Drought Monitor', 'USDA Crop Progress Reports'],
    prompt: `Track regional weather patterns, drought indices, and natural disaster risks.
Evaluate:
1. Drought or flood conditions in operating regions
2. Expected yield impact
3. Long-term climate vulnerability
Return a risk score (0-100) and structured assessment.`,
    score: 72,
    riskLevel: 'Moderate',
    summary: 'Severe drought in primary region leading to 30% yield reduction.',
    assessments: [{
      label: 'Drought Index',
      value: 'Severe (D3)',
      source: 'US Drought Monitor'
    }, {
      label: 'Yield Impact',
      value: '-30% expected',
      source: 'USDA Crop Progress Reports'
    }],
    flags: ['Severe drought conditions'],
    recommendation: 'Severe drought conditions significantly reducing expected yields will directly impact their ability to generate cash and service debt.'
  },
  'Subsidy Risk': {
    dataSources: ['USDA Farm Service Agency', 'State DEQ Records'],
    prompt: `Monitor federal crop insurance, subsidies, and EPA agricultural compliance.
Evaluate:
1. Reliance on federal subsidies
2. EPA compliance (e.g., fertilizer runoff)
3. Crop insurance coverage
Return a risk score (0-100) and structured assessment.`,
    score: 90,
    riskLevel: 'Low',
    summary: 'Stable federal subsidy reliance and clean EPA compliance record.',
    assessments: [{
      label: 'Subsidies',
      value: 'Stable, 12% of revenue',
      source: 'USDA Farm Service Agency'
    }, {
      label: 'EPA Status',
      value: 'Compliant, no violations',
      source: 'State DEQ Records'
    }],
    flags: [],
    recommendation: 'Stable reliance on federal subsidies and a clean compliance record provide a predictable baseline for revenue.'
  }
};
const chemicalIndustrialDetails: Record<string, AgentDetail> = {
  'Incident Risk': {
    dataSources: ['Google News', 'Twitter/X', 'PACER'],
    prompt: `Monitor negative press coverage, public safety incidents, and reputational damage.
Evaluate:
1. Frequency and severity of adverse media mentions
2. Public safety incidents and containment breaches
3. Active litigation from incidents
4. Social media sentiment and viral negative coverage
Return a risk score (0-100) and structured assessment.`,
    score: 15,
    riskLevel: 'High',
    summary: 'Severe reputational damage from multiple containment failures and viral social media coverage of safety incidents.',
    assessments: [{
      label: 'Media Coverage',
      value: 'Highly negative, trending nationally after "Isla Nublar incident"',
      source: 'Google News'
    }, {
      label: 'Incident Reports',
      value: '4 containment breaches in 6 months, multiple "specimen escape" events',
      source: 'Twitter/X'
    }, {
      label: 'Active Litigation',
      value: '3 wrongful death lawsuits, 12 injury claims from "attractions malfunction"',
      source: 'PACER'
    }, {
      label: 'Social Sentiment',
      value: '89% negative mentions, viral hashtag #SparedNoExpense',
      source: 'Twitter/X'
    }],
    flags: ['Viral negative coverage', 'Multiple containment failures', 'Active wrongful death suits', 'Theme park safety violations']
  },
  'Safety Compliance': {
    dataSources: ['OSHA Inspection Database', 'Google News'],
    prompt: `Track OSHA citations, safety incidents, and hazardous material handling.
Evaluate:
1. Recent OSHA citations or fines
2. Safety incident frequency
3. Hazmat storage compliance
Return a risk score (0-100) and structured assessment.`,
    score: 55,
    riskLevel: 'Moderate',
    summary: 'Recent OSHA citation for improper hazmat storage; moderate fine issued.',
    assessments: [{
      label: 'OSHA Citations',
      value: '1 active (Hazmat storage)',
      source: 'OSHA Inspection Database'
    }, {
      label: 'Fines',
      value: '$45K pending',
      source: 'Google News'
    }],
    flags: ['Active OSHA citation'],
    recommendation: 'Active OSHA citations for hazmat storage indicate operational lapses that could lead to costly fines or facility shutdowns.'
  },
  'Environmental Liability': {
    dataSources: ['State DEQ Records', 'State Court Dockets'],
    prompt: `Monitor EPA investigations, pollution fines, and remediation liabilities.
Evaluate:
1. Active EPA investigations
2. Groundwater/soil contamination liabilities
3. Pending environmental lawsuits
Return a risk score (0-100) and structured assessment.`,
    score: 40,
    riskLevel: 'High',
    summary: 'Involved in localized groundwater contamination probe by state EPA.',
    assessments: [{
      label: 'EPA Investigations',
      value: 'Active groundwater probe',
      source: 'State DEQ Records'
    }, {
      label: 'Legal Exposure',
      value: 'High potential remediation costs',
      source: 'State Court Dockets'
    }],
    flags: ['Active EPA probe', 'Potential remediation liability'],
    recommendation: 'An active EPA investigation into groundwater contamination carries the risk of massive, unpredictable remediation liabilities.'
  },
  'Raw Material Risk': {
    dataSources: ['CME Futures', 'Press Releases'],
    prompt: `Assess petrochemical feedstock costs and supplier stability.
Evaluate:
1. Feedstock price volatility
2. Ability to pass costs to buyers
3. Supplier concentration
Return a risk score (0-100) and structured assessment.`,
    score: 50,
    riskLevel: 'Moderate',
    summary: 'Petrochemical feedstocks up 22%; struggling to pass costs to buyers.',
    assessments: [{
      label: 'Feedstock Costs',
      value: '+22% this quarter',
      source: 'CME Futures'
    }, {
      label: 'Margin Impact',
      value: 'Significant compression expected',
      source: 'Press Releases'
    }],
    flags: ['Rising raw material costs'],
    recommendation: 'Rising petrochemical feedstock costs that cannot be passed to buyers will rapidly compress margins and strain cash flow.'
  }
};
const equipmentRentalDetails: Record<string, AgentDetail> = {
  'Fleet Utilization': {
    dataSources: ['IBISWorld', 'State Annual Reports'],
    prompt: `Monitor equipment utilization rates, idle time, and regional demand.
Evaluate:
1. Fleet utilization percentage
2. Regional construction demand
3. Idle asset costs
Return a risk score (0-100) and structured assessment.`,
    score: 45,
    riskLevel: 'Moderate',
    summary: 'Fleet utilization dropped to 58% due to commercial construction slowdown.',
    assessments: [{
      label: 'Utilization',
      value: '58% (down from 72%)',
      source: 'State Annual Reports'
    }, {
      label: 'Regional Demand',
      value: 'Slowing commercial starts',
      source: 'IBISWorld'
    }],
    flags: ['Dropping utilization', 'High idle assets'],
    recommendation: 'Dropping fleet utilization amid a construction slowdown means assets are sitting idle, directly impacting revenue generation.'
  },
  'Maintenance Exposure': {
    dataSources: ['UCC Database', 'Ritchie Bros Auction Data'],
    prompt: `Track fleet age, maintenance CapEx requirements, and asset depreciation.
Evaluate:
1. Average fleet age
2. Upcoming maintenance CapEx needs
3. Depreciation impact on earnings
Return a risk score (0-100) and structured assessment.`,
    score: 55,
    riskLevel: 'Moderate',
    summary: 'Average fleet age creeping to 6 years; maintenance CapEx expected to spike.',
    assessments: [{
      label: 'Fleet Age',
      value: '6.1 years average',
      source: 'UCC Database'
    }, {
      label: 'CapEx Needs',
      value: 'High near-term requirements',
      source: 'Ritchie Bros Auction Data'
    }],
    flags: ['Aging fleet', 'Impending CapEx spike'],
    recommendation: 'An aging rental fleet requires increasing maintenance CapEx, which will compete with vendor payments for available cash.'
  },
  'Product Liability': {
    dataSources: ['CPSC Database', 'PACER', 'Insurance Filings'],
    prompt: `Monitor product recalls, safety defects, and liability exposure.
Evaluate:
1. Product recall frequency and scope
2. Injury claims and settlements
3. Defect rates vs industry standards
4. Insurance coverage adequacy
Return a risk score (0-100) and structured assessment.`,
    score: 18,
    riskLevel: 'High',
    summary: 'Catastrophic product failure rates with massive liability exposure and insurance non-renewal threat.',
    assessments: [{
      label: 'Failure Rate',
      value: '3x industry average, particularly "gravity-defying" products',
      source: 'CPSC Database'
    }, {
      label: 'Injury Claims',
      value: '47 active claims: premature detonation, rocket malfunction, anvil incidents',
      source: 'PACER'
    }, {
      label: 'Recalls',
      value: 'Full anvil line recalled, TNT products under review, rocket skates banned in 12 states',
      source: 'CPSC Database'
    }, {
      label: 'Insurance Status',
      value: 'Carrier threatening non-renewal after "Wile E. Coyote" class action',
      source: 'Insurance Filings'
    }],
    flags: ['Extreme failure rates', 'Mass recall', 'Insurance crisis', 'Cartoon-level defects']
  },
  'Market Demand': {
    dataSources: ['USASpending.gov', 'IBISWorld'],
    prompt: `Monitor regional construction starts and infrastructure spending cycles.
Evaluate:
1. Infrastructure spending trends
2. Major contract wins
3. Macro cyclical risks
Return a risk score (0-100) and structured assessment.`,
    score: 88,
    riskLevel: 'Low',
    summary: 'Secured master lease agreement for major highway project amid infrastructure boom.',
    assessments: [{
      label: 'Contract Wins',
      value: 'Master lease for highway project',
      source: 'USASpending.gov'
    }, {
      label: 'Market Trend',
      value: 'Infrastructure spending up 18%',
      source: 'IBISWorld'
    }],
    flags: [],
    recommendation: 'Securing major infrastructure contracts amid a regional spending boom provides strong, predictable revenue visibility.'
  }
};
const healthcareDetails: Record<string, AgentDetail> = {
  'Malpractice Exposure': {
    dataSources: ['State Medical Board Records', 'PACER', 'Insurance Filings'],
    prompt: `Assess active malpractice suits, settlements, and liability insurance coverage.
Evaluate:
1. Active malpractice lawsuits
2. Settlement history and amounts
3. Liability insurance adequacy
4. Patient satisfaction and complaint trends
Return a risk score (0-100) and structured assessment.`,
    score: 85,
    riskLevel: 'Low',
    summary: 'Clean malpractice record with no active litigation and strong patient satisfaction scores.',
    assessments: [{
      label: 'Active Lawsuits',
      value: 'None',
      source: 'PACER'
    }, {
      label: 'Settlement History',
      value: 'No settlements in past 5 years',
      source: 'State Medical Board Records'
    }, {
      label: 'Patient Satisfaction',
      value: '4.6/5.0 average rating',
      source: 'State Medical Board Records'
    }, {
      label: 'Liability Coverage',
      value: '$5M per occurrence, $15M aggregate',
      source: 'Insurance Filings'
    }],
    flags: [],
    recommendation: 'Clean malpractice record with strong patient satisfaction and comprehensive liability coverage demonstrates low litigation risk.'
  },
  'Reimbursement Risk': {
    dataSources: ['State Annual Reports', 'IBISWorld'],
    prompt: `Monitor shifts in payer mix (Private vs Medicaid) and reimbursement cycles.
Evaluate:
1. Payer mix distribution
2. Reimbursement cycle length
3. Impact on cash conversion
Return a risk score (0-100) and structured assessment.`,
    score: 45,
    riskLevel: 'Moderate',
    summary: 'Payer mix shifting heavily to Medicaid (45%), stretching reimbursement cycles.',
    assessments: [{
      label: 'Payer Mix',
      value: '45% Medicaid (up from 30%)',
      source: 'State Annual Reports'
    }, {
      label: 'Reimbursement Cycle',
      value: 'Stretching to 65 days',
      source: 'IBISWorld'
    }],
    flags: ['Adverse payer mix shift', 'Stretching cash cycle'],
    recommendation: 'A shift toward Medicaid patients means longer reimbursement cycles, which will naturally stretch their cash conversion cycle and vendor payments.'
  },
  'Staffing Risk': {
    dataSources: ['LinkedIn Profiles', 'Glassdoor Reviews'],
    prompt: `Track nursing turnover, reliance on travel nurses, and labor cost inflation.
Evaluate:
1. Nursing turnover rate
2. Travel nurse utilization and costs
3. Impact on operating margins
Return a risk score (0-100) and structured assessment.`,
    score: 25,
    riskLevel: 'High',
    summary: 'Nursing turnover at 35%; heavy reliance on expensive travel nurses crushing margins.',
    assessments: [{
      label: 'Turnover',
      value: '35% (Nursing staff)',
      source: 'LinkedIn Profiles'
    }, {
      label: 'Labor Costs',
      value: 'Spiking due to travel nurses',
      source: 'Glassdoor Reviews'
    }],
    flags: ['High turnover', 'Unsustainable labor costs'],
    recommendation: 'High nursing turnover and reliance on expensive travel staff are structurally destroying operating margins and cash flow.'
  }
};
const retailDetails: Record<string, AgentDetail> = {
  'Environmental Impact': {
    dataSources: ['EPA Enforcement Database', 'Google News', 'ESG Ratings'],
    prompt: `Monitor environmental violations, waste management, and ESG performance.
Evaluate:
1. EPA violations and fines
2. Waste management and pollution incidents
3. ESG ratings and investor sentiment
4. Consumer boycotts related to environmental issues
Return a risk score (0-100) and structured assessment.`,
    score: 22,
    riskLevel: 'High',
    summary: 'Catastrophic environmental violations with viral negative coverage causing consumer boycotts and investor flight.',
    assessments: [{
      label: 'EPA Fines',
      value: '$12M for illegal dumping',
      source: 'EPA Enforcement Database'
    }, {
      label: 'Media Coverage',
      value: 'Viral documentary on pollution',
      source: 'Google News'
    }, {
      label: 'ESG Rating',
      value: 'F grade, bottom 5% of sector',
      source: 'ESG Ratings'
    }, {
      label: 'Investor Response',
      value: 'Major institutions divesting',
      source: 'Google News'
    }],
    flags: ['EPA violations', 'Consumer boycotts', 'ESG crisis']
  },
  'Foot Traffic': {
    dataSources: ['County Property Records', 'Local News Archives'],
    prompt: `Monitor location-based foot traffic data and YoY same-store sales trends.
Evaluate:
1. Same-store sales growth/decline
2. Foot traffic trends at physical locations
3. Market share trajectory
Return a risk score (0-100) and structured assessment.`,
    score: 35,
    riskLevel: 'High',
    summary: 'Same-store sales down 8% YoY; steady decline in mall-based foot traffic.',
    assessments: [{
      label: 'Same-Store Sales',
      value: '-8% YoY',
      source: 'Local News Archives'
    }, {
      label: 'Foot Traffic',
      value: 'Declining steadily',
      source: 'County Property Records'
    }],
    flags: ['Declining sales', 'Losing market share'],
    recommendation: 'Declining same-store sales and foot traffic indicate a loss of market share and deteriorating core business health.'
  },
  'E-Commerce Risk': {
    dataSources: ['SimilarWeb', 'State Annual Reports'],
    prompt: `Assess online sales growth, omnichannel adaptation, and digital market share.
Evaluate:
1. E-commerce revenue growth
2. Omnichannel strategy execution
3. Digital market share
Return a risk score (0-100) and structured assessment.`,
    score: 85,
    riskLevel: 'Low',
    summary: 'Online sales grew 45% this quarter, offsetting brick-and-mortar declines.',
    assessments: [{
      label: 'E-Commerce Growth',
      value: '+45% QoQ',
      source: 'State Annual Reports'
    }, {
      label: 'Omnichannel',
      value: 'Successful adaptation',
      source: 'SimilarWeb'
    }],
    flags: [],
    recommendation: 'Strong online sales growth successfully offsetting physical store declines shows effective adaptation to modern retail dynamics.'
  },
  'Stock Performance': {
    dataSources: ['Yahoo Finance', 'Bloomberg Terminal', 'S&P Global'],
    prompt: `Monitor stock performance, analyst sentiment, and market indicators for publicly traded companies.

Evaluate:
1. Stock price performance (YTD, 1-year, vs sector)
2. Analyst ratings and price targets
3. Institutional ownership trends
4. Trading volume and volatility
5. Credit rating changes

Return a risk score (0-100) and structured assessment.`,
    score: 25,
    riskLevel: 'High',
    summary: 'Stock at 5-year lows, down 32% over 12 months with dividend cut and credit downgrade.',
    assessments: [{
      label: 'Stock Performance',
      value: '-32% (12mo), 5-year low',
      source: 'Yahoo Finance'
    }, {
      label: 'Dividend',
      value: 'Cut 50% last quarter',
      source: 'Bloomberg Terminal'
    }, {
      label: 'Credit Rating',
      value: 'Downgraded to BB+ (from BBB-)',
      source: 'S&P Global'
    }, {
      label: 'Institutional Ownership',
      value: 'Declining, funds exiting',
      source: 'Yahoo Finance'
    }],
    flags: ['Multi-year lows', 'Credit downgrade', 'Dividend cut']
  }
};
const transportationDetails: Record<string, AgentDetail> = {
  'Fleet Compliance': {
    dataSources: ['FMCSA SAFER System', 'State Court Dockets'],
    prompt: `Track DOT out-of-service rates, safety violations, and inspection history.
Evaluate:
1. DOT out-of-service rate vs national average
2. Frequency of safety violations
3. Impact on operational efficiency
Return a risk score (0-100) and structured assessment.`,
    score: 45,
    riskLevel: 'Moderate',
    summary: 'DOT out-of-service rate hit 28% (vs 21% avg), triggering delays and inspections.',
    assessments: [{
      label: 'Out-of-Service Rate',
      value: '28% (High)',
      source: 'FMCSA SAFER System'
    }, {
      label: 'Operational Impact',
      value: 'Increased delays',
      source: 'State Court Dockets'
    }],
    flags: ['High DOT violation rate'],
    recommendation: 'A high DOT out-of-service rate triggers increased inspections and delays, directly impairing operational efficiency and revenue.'
  },
  'Operating Costs': {
    dataSources: ['EIA Diesel Price Index', 'State SOS Filings'],
    prompt: `Monitor diesel price exposure, fuel surcharge adequacy, and maintenance costs.
Evaluate:
1. Exposure to diesel price spikes
2. Adequacy of fuel surcharges in contracts
3. Margin impact
Return a risk score (0-100) and structured assessment.`,
    score: 55,
    riskLevel: 'Moderate',
    summary: 'Absorbing diesel price spikes due to inadequate fuel surcharges in contracts.',
    assessments: [{
      label: 'Fuel Exposure',
      value: 'High (Unhedged)',
      source: 'EIA Diesel Price Index'
    }, {
      label: 'Surcharge Adequacy',
      value: 'Inadequate for current prices',
      source: 'State SOS Filings'
    }],
    flags: ['Margin deterioration from fuel costs'],
    recommendation: 'Inadequate fuel surcharges mean the company is absorbing diesel price spikes, which will rapidly erode operating margins.'
  },
  'Labor Risk': {
    dataSources: ['Indeed / LinkedIn Jobs', 'Glassdoor Reviews'],
    prompt: `Assess driver turnover rates, wage inflation, and recruitment costs.
Evaluate:
1. Driver turnover percentage
2. Sign-on bonuses and wage inflation
3. Impact on profitability
Return a risk score (0-100) and structured assessment.`,
    score: 35,
    riskLevel: 'High',
    summary: 'Driver turnover over 90%; massive sign-on bonuses are eating profits.',
    assessments: [{
      label: 'Driver Turnover',
      value: '>90% annual',
      source: 'Glassdoor Reviews'
    }, {
      label: 'Labor Costs',
      value: 'Spiking due to bonuses',
      source: 'Indeed / LinkedIn Jobs'
    }],
    flags: ['Extreme turnover', 'Unsustainable labor costs'],
    recommendation: 'Extreme driver turnover and massive sign-on bonuses are structurally unsustainable and will consume available cash flow.'
  },
  'Customer Concentration (Transportation)': {
    dataSources: ['Press Releases', 'State Annual Reports'],
    prompt: `Track dedicated lane contracts, spot market reliance, and key customer health.
Evaluate:
1. Major contract wins or losses
2. Reliance on spot market vs dedicated lanes
3. Revenue stability
Return a risk score (0-100) and structured assessment.`,
    score: 92,
    riskLevel: 'Low',
    summary: 'Won dedicated lane contract with major e-commerce platform. Massive revenue boost.',
    assessments: [{
      label: 'Contract Wins',
      value: 'Amazoon dedicated lanes (50 ships), includes Earth-Mars route',
      source: 'Press Releases'
    }, {
      label: 'Revenue Impact',
      value: 'CEO quoted: "bigger than that time we delivered to Omicron Persei 8"',
      source: 'Press Releases'
    }, {
      label: 'Fleet Status',
      value: 'Primary delivery ship operational, certified for interplanetary commerce',
      source: 'State Annual Reports'
    }, {
      label: 'Delivery Record',
      value: '94% on-time delivery rate across all planetary routes',
      source: 'State Annual Reports'
    }],
    flags: []
  }
};
const industryAgentDetails: Record<string, Record<string, AgentDetail>> = {
  Agriculture: agricultureDetails,
  'Chemical & Industrial': chemicalIndustrialDetails,
  'Construction & Building Materials': buildingMaterialsDetails,
  Distribution: distributionDetails,
  'Equipment Rental & Leasing': equipmentRentalDetails,
  'Food & Beverage': foodBeverageDetails,
  Healthcare: healthcareDetails,
  Manufacturing: manufacturingDetails,
  Retail: retailDetails,
  'Software (SaaS)': softwareDetails,
  'Transportation & Logistics': transportationDetails
};
const arDataByIndustry: Record<string, ARData> = {
  Agriculture: {
    outstanding: '$312,000',
    current: 45,
    days30: 30,
    days60: 15,
    days90: 10,
    terms: 'Net 60',
    limit: '$400,000',
    utilization: 78
  },
  'Chemical & Industrial': {
    outstanding: '$540,000',
    current: 65,
    days30: 20,
    days60: 10,
    days90: 5,
    terms: 'Net 45',
    limit: '$750,000',
    utilization: 72
  },
  'Construction & Building Materials': {
    outstanding: '$142,000',
    current: 60,
    days30: 22,
    days60: 12,
    days90: 6,
    terms: 'Net 30',
    limit: '$150,000',
    utilization: 94
  },
  Distribution: {
    outstanding: '$215,000',
    current: 55,
    days30: 20,
    days60: 15,
    days90: 10,
    terms: 'Net 30',
    limit: '$250,000',
    utilization: 86
  },
  'Equipment Rental & Leasing': {
    outstanding: '$185,000',
    current: 75,
    days30: 15,
    days60: 8,
    days90: 2,
    terms: 'Net 30',
    limit: '$300,000',
    utilization: 61
  },
  'Food & Beverage': {
    outstanding: '$178,000',
    current: 65,
    days30: 20,
    days60: 10,
    days90: 5,
    terms: 'Net 30',
    limit: '$200,000',
    utilization: 89
  },
  Healthcare: {
    outstanding: '$890,000',
    current: 40,
    days30: 25,
    days60: 20,
    days90: 15,
    terms: 'Net 60',
    limit: '$1,000,000',
    utilization: 89
  },
  Manufacturing: {
    outstanding: '$89,000',
    current: 70,
    days30: 18,
    days60: 8,
    days90: 4,
    terms: 'Net 45',
    limit: '$200,000',
    utilization: 44
  },
  Retail: {
    outstanding: '$420,000',
    current: 50,
    days30: 25,
    days60: 15,
    days90: 10,
    terms: 'Net 30',
    limit: '$500,000',
    utilization: 84
  },
  'Software (SaaS)': {
    outstanding: '$45,000',
    current: 80,
    days30: 15,
    days60: 5,
    days90: 0,
    terms: 'Net 30',
    limit: '$75,000',
    utilization: 60
  },
  'Transportation & Logistics': {
    outstanding: '$275,000',
    current: 68,
    days30: 20,
    days60: 8,
    days90: 4,
    terms: 'Net 30',
    limit: '$400,000',
    utilization: 68
  },
  Other: {
    outstanding: '$98,000',
    current: 72,
    days30: 16,
    days60: 8,
    days90: 4,
    terms: 'Net 30',
    limit: '$150,000',
    utilization: 65
  }
};
const riskColor = (level: string) => {
  if (level === 'Low') return {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
    dot: 'bg-green-500'
  };
  if (level === 'Moderate') return {
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    dot: 'bg-yellow-500'
  };
  return {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    dot: 'bg-red-500'
  };
};
const INDUSTRIES = ['Agriculture', 'Chemical & Industrial', 'Construction & Building Materials', 'Distribution', 'Equipment Rental & Leasing', 'Food & Beverage', 'Healthcare', 'Manufacturing', 'Retail', 'Software (SaaS)', 'Transportation & Logistics', 'Other'] as const;
const PULSE_SCORES: Record<string, {
  score: number;
  level: 'Low' | 'Moderate' | 'High';
}> = {
  Agriculture: {
    score: 66,
    level: 'Moderate'
  },
  'Chemical & Industrial': {
    score: 60,
    level: 'Moderate'
  },
  'Construction & Building Materials': {
    score: 72,
    level: 'Low'
  },
  Distribution: {
    score: 58,
    level: 'Moderate'
  },
  'Equipment Rental & Leasing': {
    score: 70,
    level: 'Low'
  },
  'Food & Beverage': {
    score: 55,
    level: 'Moderate'
  },
  Healthcare: {
    score: 52,
    level: 'Moderate'
  },
  Manufacturing: {
    score: 64,
    level: 'Moderate'
  },
  Retail: {
    score: 58,
    level: 'Moderate'
  },
  'Software (SaaS)': {
    score: 48,
    level: 'Moderate'
  },
  'Transportation & Logistics': {
    score: 62,
    level: 'Moderate'
  },
  Other: {
    score: 68,
    level: 'Moderate'
  }
};
const COMPANY_NAMES: Record<string, string> = {
  Agriculture: 'Schrute Farms',
  'Chemical & Industrial': 'InGen Corporation',
  'Construction & Building Materials': 'Bluth Company',
  Distribution: 'Dunder Mifflin Paper Company',
  'Equipment Rental & Leasing': 'ACME Corporation',
  'Food & Beverage': 'Los Pollos Hermanos',
  Healthcare: 'Sacred Heart Hospital',
  Manufacturing: 'Stark Industries',
  Retail: 'Buy n Large',
  'Software (SaaS)': 'Pied Piper',
  'Transportation & Logistics': 'Planet Express'
};
const COMPANY_DESCRIPTIONS: Record<string, string> = {
  Agriculture: 'Family-owned beet farm and bed & breakfast',
  'Chemical & Industrial': 'Genetic engineering and dinosaur theme park operator',
  'Construction & Building Materials': 'Family-owned real estate development company',
  Distribution: 'Regional paper and office supply distributor',
  'Equipment Rental & Leasing': 'Industrial equipment and novelty items supplier',
  'Food & Beverage': 'Fast-food chicken restaurant chain',
  Healthcare: 'Academic teaching hospital',
  Manufacturing: 'Advanced technology and defense contractor',
  Retail: 'Mega-corporation retail conglomerate',
  'Software (SaaS)': 'Middle-out compression technology platform',
  'Transportation & Logistics': 'Interplanetary package delivery service'
};
export function AdminDashboard() {
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [showIndustryPicker, setShowIndustryPicker] = useState(true);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');
  const [showScout, setShowScout] = useState(false);
  const agents = industryAgents[selectedIndustry] || defaultAgents;
  const agentDetails = industryAgentDetails[selectedIndustry] || defaultAgentDetails;
  const detail = selectedAgent ? agentDetails[selectedAgent] : null;
  const scoutMessage = selectedAgent ? scoutInsights[selectedAgent] : null;
  const currentAR = arDataByIndustry[selectedIndustry] || arDataByIndustry['Other'];
  // Auto-open Scout 2 seconds after opening an agent modal
  useEffect(() => {
    if (selectedAgent) {
      setShowScout(false);
      const timer = setTimeout(() => setShowScout(true), 2000);
      return () => clearTimeout(timer);
    } else {
      setShowScout(false);
    }
  }, [selectedAgent]);
  return <div className="p-8 max-w-[1400px] mx-auto w-full" data-id="element-31">
      <h1 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-32">
        Super Admin Dashboard
      </h1>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-33">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 overflow-x-auto" data-id="element-34">
          <button className="flex items-center gap-2 px-6 py-4 text-sm font-semibold text-gray-500 hover:text-gray-700 whitespace-nowrap" data-id="element-35">
            <LayoutDashboard className="w-4 h-4" data-id="element-36" />
            USAGE DASHBOARD
          </button>
          <button className="flex items-center gap-2 px-6 py-4 text-sm font-semibold text-gray-500 hover:text-gray-700 whitespace-nowrap" data-id="element-37">
            <Receipt className="w-4 h-4" data-id="element-38" />
            BILLING
          </button>
          <button className="flex items-center gap-2 px-6 py-4 text-sm font-semibold text-gray-500 hover:text-gray-700 whitespace-nowrap" data-id="element-39">
            <Key className="w-4 h-4" data-id="element-40" />
            API KEYS
          </button>
          <button className="flex items-center gap-2 px-6 py-4 text-sm font-semibold text-gray-500 hover:text-gray-700 whitespace-nowrap" data-id="element-41">
            <Users className="w-4 h-4" data-id="element-42" />
            GROUPS
          </button>
          <button className="flex items-center gap-2 px-6 py-4 text-sm font-semibold text-indigo-600 border-b-2 border-indigo-600 whitespace-nowrap" data-id="element-43">
            <Bot className="w-4 h-4" data-id="element-44" />
            AGENTS
          </button>
        </div>

        {/* Content */}
        <div className="p-6" data-id="element-45">
          <div className="flex items-center justify-between mb-6" data-id="element-46">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-47">AI Agents</h2>
            <div className="flex items-center gap-3" data-id="element-48">
              {!showIndustryPicker && <button onClick={() => setShowIndustryPicker(true)} className="flex items-center gap-2 border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-id="element-49">
                  <Bot className="w-4 h-4" data-id="element-50" />
                  CHANGE INDUSTRY
                </button>}
              <button className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-id="element-51">
                <Plus className="w-4 h-4" data-id="element-52" />
                CREATE AGENT
              </button>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-53">
            <table className="w-full text-left text-sm" data-id="element-54">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-55">
                <tr data-id="element-56">
                  <th className="px-6 py-4 font-bold text-gray-900 w-[25%]" data-id="element-57">
                    Name
                  </th>
                  <th className="px-6 py-4 font-bold text-gray-900 w-[12%]" data-id="element-58">
                    Status
                  </th>
                  <th className="px-6 py-4 font-bold text-gray-900 w-[38%]" data-id="element-59">
                    Description
                  </th>
                  <th className="px-6 py-4 font-bold text-gray-900 w-[15%]" data-id="element-60">
                    Created
                  </th>
                  <th className="px-6 py-4 font-bold text-gray-900 w-[5%]" data-id="element-61">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-62">
                {agents.map((agent, idx) => <tr key={idx} className="hover:bg-gray-50 transition-colors" data-id="element-63">
                    <td className="px-6 py-4 align-top" data-id="element-64">
                      <div className="flex items-center gap-2" data-id="element-65">
                        <button onClick={() => setSelectedAgent(agent.name)} className="text-indigo-600 font-medium hover:underline text-left" data-id="element-66">
                          {agent.name}
                        </button>
                        {idx === 0 && !showIndustryPicker && <motion.span initial={{
                      opacity: 0,
                      x: -5
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: 0.5,
                      duration: 0.3
                    }} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-600 whitespace-nowrap" data-id="element-67">
                            <MousePointerClick className="w-3 h-3" data-id="element-68" />
                            click to preview
                          </motion.span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top" data-id="element-69">
                      <div className="flex items-center gap-2" data-id="element-70">
                        {agent.status === 'Active' ? <div className="w-10 h-5 bg-green-600 rounded-full relative flex items-center shrink-0" data-id="element-71">
                            <div className="w-3.5 h-3.5 bg-white rounded-full absolute right-1" data-id="element-72" />
                          </div> : <div className="w-10 h-5 bg-gray-300 rounded-full relative flex items-center shrink-0" data-id="element-73">
                            <div className="w-3.5 h-3.5 bg-white rounded-full absolute left-1" data-id="element-74" />
                          </div>}
                        <span className="text-gray-700" data-id="element-75">{agent.status}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top text-gray-500 leading-relaxed" data-id="element-76">
                      {agent.description}
                    </td>
                    <td className="px-6 py-4 align-top text-gray-700" data-id="element-77">
                      {agent.created}
                    </td>
                    <td className="px-6 py-4 align-top" data-id="element-78">
                      <button className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100" data-id="element-79">
                        <MoreVertical className="w-5 h-5" data-id="element-80" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Industry Picker Modal */}
      <AnimatePresence data-id="element-81">
        {showIndustryPicker && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-82">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-83">
              <div className="px-6 pt-6 pb-2" data-id="element-84">
                <div className="w-11 h-11 bg-indigo-100 rounded-xl flex items-center justify-center mb-4" data-id="element-85">
                  <Bot className="w-6 h-6 text-indigo-600" data-id="element-86" />
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-87">
                  Welcome to AI Agents
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-88">
                  To test this feature, select your industry below and we'll
                  show you the closest use case with real agent configurations
                  and example outputs.
                </p>
              </div>

              <div className="px-6 py-4" data-id="element-89">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-90">
                  Industry
                </label>
                <select value={selectedIndustry} onChange={e => setSelectedIndustry(e.target.value)} className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm transition-colors cursor-pointer" data-id="element-91">
                  <option value="" disabled className="text-gray-400" data-id="element-92">
                    Select your industry…
                  </option>
                  {INDUSTRIES.map(industry => <option key={industry} value={industry} className="text-gray-900" data-id="element-93">
                      {industry}
                    </option>)}
                </select>
              </div>

              <div className="px-6 pb-6 pt-2" data-id="element-94">
                <button onClick={() => {
              if (selectedIndustry) setShowIndustryPicker(false);
            }} disabled={!selectedIndustry} className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${selectedIndustry ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-95">
                  Continue
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Agent Detail Modal */}
      <AnimatePresence data-id="element-96">
        {selectedAgent && detail && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedAgent(null)} data-id="element-97">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-98">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-99">
                <div className="flex items-center gap-3" data-id="element-100">
                  <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center" data-id="element-101">
                    <Bot className="w-5 h-5 text-indigo-600" data-id="element-102" />
                  </div>
                  <div data-id="element-103">
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-104">
                      {selectedAgent}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-105">
                      Agent Configuration & Example Output
                    </p>
                  </div>
                </div>
                <button onClick={() => setSelectedAgent(null)} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-106">
                  <X className="w-5 h-5" data-id="element-107" />
                </button>
              </div>

              {/* Modal Body — Two Panels */}
              <div className="flex-1 overflow-auto" data-id="element-108">
                <div className="grid grid-cols-2 divide-x divide-gray-200 min-h-0" data-id="element-109">
                  {/* Left Panel: Configuration */}
                  <div className="p-6" data-id="element-110">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-111">
                      Configuration
                    </h4>

                    <div className="mb-5" data-id="element-112">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-113">
                        Data Sources
                      </label>
                      <div className="flex flex-wrap gap-2" data-id="element-114">
                        {[...new Set(detail.dataSources)].map((source, i) => <span key={i} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200" data-id="element-115">
                            {source}
                          </span>)}
                      </div>
                    </div>

                    <div data-id="element-116">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-117">
                        Prompt
                      </label>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-118">
                        <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed" data-id="element-119">
                          {detail.prompt}
                        </pre>
                      </div>
                    </div>
                  </div>

                  {/* Right Panel: Example Output — Readable Card */}
                  <div className="p-6 bg-gray-50" data-id="element-120">
                    <div className="mb-4" data-id="element-121">
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-122">
                        Example Output
                      </h4>
                      <div className="flex items-center justify-between gap-3 mb-1" data-id="element-123">
                        <p className="text-2xl font-bold text-gray-900" data-id="element-124">
                          {COMPANY_NAMES[selectedIndustry] || 'Dunder Mifflin Paper Company'}
                        </p>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold border-2 shrink-0 ${riskColor(detail.riskLevel).bg} ${riskColor(detail.riskLevel).text} ${riskColor(detail.riskLevel).border}`} data-id="element-125">
                          <span className={`w-2 h-2 rounded-full ${riskColor(detail.riskLevel).dot}`} data-id="element-126" />
                          {detail.riskLevel} Risk
                        </span>
                      </div>
                      <p className="text-sm text-gray-500" data-id="element-127">
                        {COMPANY_DESCRIPTIONS[selectedIndustry] || 'Regional paper and office supply distributor'}
                      </p>
                    </div>

                    {/* Agent Summary */}
                    <div className="mb-4" data-id="element-128">
                      <p className="text-sm text-gray-600" data-id="element-129">{detail.summary}</p>
                    </div>

                    {/* Assessment Rows */}
                    <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 mb-4" data-id="element-130">
                      {detail.assessments.map((item, i) => <div key={i} className="flex items-start justify-between px-4 py-3" data-id="element-131">
                          <div className="flex-1 min-w-0" data-id="element-132">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide" data-id="element-133">
                              {item.label}
                            </p>
                            <p className="text-sm text-gray-800 mt-0.5" data-id="element-134">
                              {item.value}
                            </p>
                          </div>
                          {item.source && <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-100 shrink-0 ml-3 mt-1" data-id="element-135">
                              {item.source}
                            </span>}
                        </div>)}
                    </div>

                    {/* Flags */}
                    {detail.flags.length > 0 && <div className="mb-4" data-id="element-136">
                        <div className="flex items-center gap-1.5 mb-2" data-id="element-137">
                          <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" data-id="element-138" />
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-139">
                            Flags
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2" data-id="element-140">
                          {detail.flags.map((flag, i) => <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200" data-id="element-141">
                              {flag}
                            </span>)}
                        </div>
                      </div>}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Scout Chat Bubble */}
            <AnimatePresence data-id="element-142">
              {showScout && scoutMessage && <motion.div initial={{
            opacity: 0,
            y: 40,
            scale: 0.9
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 20,
            scale: 0.95
          }} transition={{
            type: 'spring',
            damping: 20,
            stiffness: 300
          }} className="fixed bottom-6 right-6 z-[60] w-[380px]" onClick={e => e.stopPropagation()} data-id="element-143">
                  <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden" data-id="element-144">
                    {/* Scout Header */}
                    <div className="bg-gradient-to-r from-[#4a7c6f] to-[#3d5a80] px-4 py-3 flex items-center gap-3" data-id="element-145">
                      <img src={SCOUT_AVATAR} alt="Scout" className="w-10 h-10 rounded-full border-2 border-white/30" data-id="element-146" />
                      <div className="flex-1" data-id="element-147">
                        <p className="text-white text-lg font-bold" data-id="element-148">Scout</p>
                        <p className="text-white/70 text-xs" data-id="element-149">
                          Your Credit Copilot
                        </p>
                      </div>
                      <button onClick={() => setShowScout(false)} className="text-white/60 hover:text-white transition-colors" data-id="element-150">
                        <X className="w-4 h-4" data-id="element-151" />
                      </button>
                    </div>

                    {/* Scout Message */}
                    <div className="p-4" data-id="element-152">
                      <div className="flex gap-3" data-id="element-153">
                        <img src={SCOUT_AVATAR} alt="Scout" className="w-9 h-9 rounded-full shrink-0 mt-0.5" data-id="element-154" />
                        <div className="bg-gray-50 rounded-xl rounded-tl-sm px-3.5 py-2.5 border border-gray-100" data-id="element-155">
                          <p className="text-sm text-gray-700 leading-snug" data-id="element-156">
                            {scoutMessage}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Scout Input */}
                    <div className="px-4 pb-3" data-id="element-157">
                      <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 border border-gray-200" data-id="element-158">
                        <input type="text" placeholder="Ask Scout a follow-up…" className="flex-1 bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none" data-id="element-159" />
                        <button className="text-indigo-500 hover:text-indigo-600 transition-colors" data-id="element-160">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-161">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" data-id="element-162" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>}
      </AnimatePresence>
    </div>;
}